import { SubmitHandler, useForm } from "react-hook-form";
import Grid from "@mui/material/Grid";

import CategorySelectForm from "../../../categories/components/CategorySelectForm";
import DomainSelectForm from "../../../domain/components/form/DomainSelectForm";
import RichTextForm from "../../../../components/reusable-components/form/RichTextForm";
import FormTextField from "../../../../components/reusable-components/form/MuiFormInput";
import SubmitButton from "../../../../components/reusable-components/form/SubmitButton";
import FormCardWrapper from "../../../../components/reusable-components/form/FormCardWrapper";

import { useApiFieldErrors } from "../../../../hooks/fetch/useApiFieldErrors";
import {
  ProductCreate,
  ProductCreateConstructor,
} from "../../../product/models/ProductCreate";
import { useCreateProduct } from "../../hooks/useCreateProduct";
import { useNavigate } from "react-router-dom";
import { useFieldRules } from "../../../../hooks/useFieldRules";
import EntitySettingsForm from "../../../setting/components/EntitySettingsForm";
import { EntityTypeLink } from "../../../setting/models/enums/EntityTypeLink";
import { Box, CardMedia } from "@mui/material";

const CreateProduct = () => {
  const {
    fetch: {
      fetchData: createProduct,
      loading: creating,
      error: createError,
      success: createSuccess,
    },
  } = useCreateProduct();

  const rules = useFieldRules();

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    clearErrors,
    control,
    setValue,
    watch,
  } = useForm<ProductCreateConstructor>({
    defaultValues: {
      name: "",
      slug: "",
      short_description: "",
      description: "",
      price: 0,
      sale_price: undefined,
      domain_id: undefined,
      category_id: undefined,
      image_url: "",
      brand_name: "",
      stock_quantity: 0,
      rating: undefined,
      affiliate_link: "",
    },
    mode: "all",
  });

  useApiFieldErrors(createError, setError);

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<ProductCreateConstructor> = async (
    formData
  ) => {
    clearErrors();
    const response = await createProduct({ body: new ProductCreate(formData) });

    if (response?.id) {
      navigate(`/product/edit/${response.id}`);
    }
  };

  const isLoading = creating || isSubmitting;
  const featuredImage = watch("image_url");

  return (
    <FormCardWrapper
      title="Create Product"
      successText="Product created successfully!"
      success={createSuccess}
      error={createError}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="name"
            label="Name"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Name").required().maxLength(255).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="slug"
            label="Slug"
            control={control}
            textFieldProps={{
              placeholder: "Enter slug...",
              disabled: isLoading,
            }}
            rules={rules("Slug").required().slug().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="price"
            label="Price"
            control={control}
            textFieldProps={{
              type: "number",
              inputProps: { step: "0.01", min: 0 },
              disabled: isLoading,
            }}
            rules={rules("Price").required().min(0).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="sale_price"
            label="Sale Price"
            control={control}
            textFieldProps={{
              type: "number",
              inputProps: { step: "0.01", min: 0 },
              disabled: isLoading,
            }}
            rules={rules("Sale Price").min(0).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DomainSelectForm
            name="domain_id"
            control={control}
            disabled={isLoading}
            controllerProps={{
              rules: rules("Domain").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CategorySelectForm
            name="category_id"
            control={control}
            setValue={setValue}
            watch={watch}
            domainId={watch("domain_id")}
            disabled={isLoading || !watch("domain_id")?.value}
            controllerProps={{
              rules: rules("Category").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="brand_name"
            label="Brand Name"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Brand Name").required().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="stock_quantity"
            label="Stock Quantity"
            control={control}
            textFieldProps={{ type: "number", disabled: isLoading }}
            rules={rules("Stock Quantity").required().min(0).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="rating"
            label="Rating"
            control={control}
            textFieldProps={{ type: "number", disabled: isLoading }}
            rules={rules("Rating").min(0).max(5).get()}
          />
        </Grid>

        <Grid size={12}>
          <FormTextField
            name="affiliate_link"
            label="Affiliate Link"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Affiliate Link").url().required().get()}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="image_url"
            label="Image URL"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Image URL").required().url().get()}
          />
        </Grid>
      </Grid>
      {featuredImage && (
        <Box mt={2}>
          <CardMedia
            component="img"
            height="200"
            image={featuredImage}
            alt="Featured Preview"
            sx={{
              borderRadius: 2,
              border: "1px solid #ccc",
              objectFit: "contain",
              maxWidth: "100%",
            }}
          />
        </Box>
      )}
      <RichTextForm
        name="short_description"
        control={control}
        label="Short Description"
        disabled={isLoading}
        rules={rules("Short Description").required().get()}
      />

      <RichTextForm
        name="description"
        control={control}
        label="Description"
        disabled={isLoading}
        rules={rules("Description").required().get()}
      />
      <EntitySettingsForm
        control={control}
        entityType={EntityTypeLink.PRODUCTS}
        disabled={isLoading}
      />
      <SubmitButton
        text="Create Product"
        loading={isLoading}
        disabled={isLoading}
      />
    </FormCardWrapper>
  );
};

export default CreateProduct;
